import { api } from '../../lib/apiClient.js';
import { FEEDBACK_STORAGE_KEYS } from './feedbackTypes.js';
import { isFeedbackTestHookEnabled } from './feedbackTestHooks.js';

// The governed comment path enforces one shared character bound on reviewer
// notes, reporter replies, AND the request-more-info note (server:
// normalizeFeedbackCommentBody). Mirror it on the client so oversize text is
// caught inline instead of reaching an ambiguous server 400 that discards the
// author's typed work.
export const FEEDBACK_COMMENT_MAX_LENGTH = 4000;

// The server REQUIRES a UUID Idempotency-Key on POST /feedback/:id/comment
// (same contract as convert / request-more-info). Callers that manage a
// retained key across ambiguous retries pass it in; callers that do not still
// get a fresh key so a committed-but-response-lost comment replays the ORIGINAL
// comment instead of duplicating the message, its audit, and a revision.
function newCommentIdempotencyKey() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  // Browser/Node crypto is present in supported PRIME environments; this keeps
  // UUID shape for older local test runners only.
  return '00000000-0000-4000-8000-'.concat(
    Math.random().toString(16).slice(2).padEnd(12, '0').slice(0, 12),
  );
}

function failureHeaders(kind) {
  const headers = {};
  if (kind === 'save' && isFeedbackTestHookEnabled(FEEDBACK_STORAGE_KEYS.failSave)) {
    headers['x-feedback-test-fail'] = 'save';
  }
  if (kind === 'history' && isFeedbackTestHookEnabled(FEEDBACK_STORAGE_KEYS.failHistory)) {
    headers['x-feedback-test-fail'] = 'history';
  }
  return headers;
}

export async function listFeedbackPackages(query = {}) {
  return api.get('/feedback-packages', {
    query: {
      ...query,
      scope: query.scope,
    },
    headers: failureHeaders('history'),
  });
}

export async function createFeedbackPackage(payload, idempotencyKey) {
  return api.post('/feedback-packages', payload, {
    headers: {
      ...failureHeaders('save'),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
  });
}

export async function getFeedbackPackage(packageId) {
  return api.get(`/feedback-packages/${encodeURIComponent(packageId)}`);
}

export async function patchFeedbackPackage(packageId, payload) {
  return api.patch(`/feedback-packages/${encodeURIComponent(packageId)}`, payload, {
    headers: failureHeaders('save'),
  });
}

export async function addFeedbackCapture(packageId, capture) {
  return api.post(`/feedback-packages/${encodeURIComponent(packageId)}/captures`, capture, {
    headers: failureHeaders('save'),
  });
}

export async function updateFeedbackCapture(packageId, captureId, capture) {
  return api.patch(
    `/feedback-packages/${encodeURIComponent(packageId)}/captures/${encodeURIComponent(captureId)}`,
    capture,
    { headers: failureHeaders('save') },
  );
}

export async function deleteFeedbackCapture(packageId, captureId, payload) {
  const { idempotencyKey, ...body } = payload || {};
  return api.delete(
    `/feedback-packages/${encodeURIComponent(packageId)}/captures/${encodeURIComponent(captureId)}`,
    { body, headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {} },
  );
}

export async function finalizeFeedbackPackage(packageId, payload) {
  return api.post(`/feedback-packages/${encodeURIComponent(packageId)}/finalize`, payload, {
    headers: failureHeaders('save'),
  });
}

export async function submitFeedbackPackage(packageId, payload) {
  const { idempotencyKey, ...body } = payload || {};
  return api.post(`/feedback-packages/${encodeURIComponent(packageId)}/submit`, body, {
    headers: {
      ...failureHeaders('save'),
      ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
    },
  });
}

export async function convertGovernedFeedback(feedbackId, payload) {
  const { idempotencyKey, ...body } = payload || {};
  return api.post(`/feedback/${encodeURIComponent(feedbackId)}/convert`, body, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  });
}

export async function requestGovernedFeedbackInfo(feedbackId, payload) {
  const { idempotencyKey, ...body } = payload || {};
  return api.post(`/feedback/${encodeURIComponent(feedbackId)}/request-more-info`, body, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  });
}

export async function deleteFeedbackPackage(packageId) {
  return api.delete(`/feedback-packages/${encodeURIComponent(packageId)}`);
}

// Governed feedback records (owner-scoped for non-reviewers) — the tester's
// view of what happened to each submitted mark (W3A My feedback page).
export async function listGovernedFeedback(query = {}) {
  return api.get('/feedback', { query });
}

// Record detail includes the comment thread (product-team replies) and audit
// history; readable by the reporter or a reviewer.
export async function getGovernedFeedback(recordId) {
  return api.get(`/feedback/${encodeURIComponent(recordId)}`);
}

// W3B reply loop: the reporter can reply on their own record (until it
// reaches a terminal status); reviewers can always comment. The server
// requires a UUID Idempotency-Key here, so one is always sent — a retained key
// (passed by the caller) makes a retry after a lost response replay the
// original comment; otherwise a fresh key is generated.
export async function commentOnGovernedFeedback(recordId, body, { idempotencyKey } = {}) {
  return api.post(
    `/feedback/${encodeURIComponent(recordId)}/comment`,
    { body },
    { headers: { 'Idempotency-Key': idempotencyKey || newCommentIdempotencyKey() } },
  );
}
